import { useEffect, useMemo, type CSSProperties } from 'react';
import { usePrimaryColor, useSecondaryColor } from '@vibesites/runtime/react';
import { getContrastTextColor } from '../utils/luminance';

const STYLE_ID = 'homestead-cta-hover';

/**
 * Shared Homestead CTA hover treatment (color swap + lift + shadow).
 *
 * Inheritance:
 * - `src/registry.ts` side-imports `src/index.css` so child sites that spread
 *   this company registry get the rules in their Vite/SSG CSS bundle.
 * - Keep this string in sync with `src/index.css`. The editor / some hosts
 *   load the registry without a CSS pipeline — Header + Footer call
 *   {@link useCtaColors} to inject the same rules at runtime.
 *
 * Transition syntax: each property must carry its own duration/easing.
 * (`transition: a, b, c 0.3s` is invalid and silently drops the first props.)
 */
export const CTA_HOVER_CSS = `
/* ── Homestead CTA hover (global) ── */
.header-cta--solid,
.drawer-cta,
.overview-cta,
.parallax-cta--solid,
.fp-btn--filled,
.footer-cort-cta,
.pet-cta,
.frc-cta,
.hero-callout-link,
.cf-form-wrap .mh-lead-form-submit-button,
.cf-form-wrap .mh-lead-form-success-cta,
.stf-form-wrap button.ml-auto.bg-primary,
.stf-form-wrap a.bg-primary {
  transition:
    background-color 0.3s ease,
    color 0.3s ease,
    border-color 0.3s ease,
    transform 0.3s ease,
    box-shadow 0.3s ease,
    filter 0.3s ease;
}

/* Solid secondary → primary */
.header-cta--solid:hover,
.drawer-cta:hover,
.overview-cta:hover,
.parallax-cta--solid:hover,
.fp-btn--filled:hover,
.footer-cort-cta:hover {
  background: var(--vibe-color-primary) !important;
  background-color: var(--vibe-color-primary) !important;
  color: var(--homestead-cta-on-primary, var(--vibe-color-text-on-primary)) !important;
  border-color: var(--vibe-color-primary);
  transform: translateY(-2px);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
}

.header-cta--solid:active,
.drawer-cta:active,
.overview-cta:active,
.parallax-cta--solid:active,
.fp-btn--filled:active,
.footer-cort-cta:active {
  transform: translateY(0);
  box-shadow: none;
}

/* Outline / text CTAs → brand fill + lift */
.pet-cta:hover,
.frc-cta:hover {
  background: var(--vibe-color-secondary);
  color: var(--homestead-cta-on-secondary, var(--vibe-color-text-on-secondary, #fff));
  border-color: var(--vibe-color-secondary);
  transform: translateY(-2px);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
}

.pet-cta:active,
.frc-cta:active {
  transform: translateY(0);
  box-shadow: none;
}

/* Hero callout strip → secondary */
.hero-callout-link:hover {
  background: var(--vibe-color-secondary);
  color: var(--homestead-cta-on-secondary, var(--vibe-color-text-on-secondary, #fff));
  transform: translateY(-2px);
  box-shadow: 0 8px 22px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
  position: relative;
  z-index: 1;
}

.hero-callout-link:active {
  transform: translateY(0);
  box-shadow: none;
}

/* Primary form CTAs → secondary (inline Multihub colors need !important) */
.cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled),
.cf-form-wrap .mh-lead-form-success-cta:hover,
.stf-form-wrap button.ml-auto.bg-primary:hover:not(:disabled),
.stf-form-wrap a.bg-primary:hover {
  background-color: var(--vibe-color-secondary) !important;
  color: var(--homestead-cta-on-secondary, var(--vibe-color-text-on-secondary, #fff)) !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-secondary) 40%, transparent);
}

.cf-form-wrap .mh-lead-form-submit-button:active:not(:disabled),
.cf-form-wrap .mh-lead-form-success-cta:active,
.stf-form-wrap button.ml-auto.bg-primary:active:not(:disabled),
.stf-form-wrap a.bg-primary:active {
  transform: translateY(0);
  box-shadow: none;
}

@media (prefers-reduced-motion: reduce) {
  .header-cta--solid:hover,
  .header-cta--solid:active,
  .drawer-cta:hover,
  .drawer-cta:active,
  .overview-cta:hover,
  .overview-cta:active,
  .parallax-cta--solid:hover,
  .parallax-cta--solid:active,
  .fp-btn--filled:hover,
  .fp-btn--filled:active,
  .footer-cort-cta:hover,
  .footer-cort-cta:active,
  .pet-cta:hover,
  .pet-cta:active,
  .frc-cta:hover,
  .frc-cta:active,
  .hero-callout-link:hover,
  .hero-callout-link:active,
  .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled),
  .cf-form-wrap .mh-lead-form-submit-button:active:not(:disabled),
  .cf-form-wrap .mh-lead-form-success-cta:hover,
  .cf-form-wrap .mh-lead-form-success-cta:active,
  .stf-form-wrap button.ml-auto.bg-primary:hover:not(:disabled),
  .stf-form-wrap button.ml-auto.bg-primary:active:not(:disabled),
  .stf-form-wrap a.bg-primary:hover,
  .stf-form-wrap a.bg-primary:active {
    transform: none;
    box-shadow: none;
  }
}
`;

export type CtaColorVars = CSSProperties & {
  ['--homestead-cta-on-primary']?: string;
  ['--homestead-cta-on-secondary']?: string;
  ['--homestead-util-on-secondary']?: string;
};

/**
 * Contrast-picked CTA text colors + one-time injection of {@link CTA_HOVER_CSS}.
 * Call from Header and Footer (both always mounted via DefaultLayout) so the
 * rules survive a child-site override of either one.
 *
 * Vars are written onto `:root` (not just the header node) so Footer / body
 * CTAs outside `<header>` still inherit them.
 */
export function useCtaColors(): CtaColorVars {
  const primaryColor = usePrimaryColor();
  const secondaryColor = useSecondaryColor();

  const vars = useMemo(() => {
    const onPrimary = getContrastTextColor(primaryColor);
    const onSecondary = getContrastTextColor(secondaryColor);
    return {
      ['--homestead-cta-on-primary']: onPrimary,
      ['--homestead-cta-on-secondary']: onSecondary,
      // Alias used by existing Header util-bar / drawer CTA rules.
      ['--homestead-util-on-secondary']: onSecondary,
    } satisfies CtaColorVars;
  }, [primaryColor, secondaryColor]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CTA_HOVER_CSS;
      document.head.appendChild(style);
    }

    const root = document.documentElement;
    for (const [key, value] of Object.entries(vars)) {
      if (typeof value === 'string') root.style.setProperty(key, value);
    }
  }, [vars]);

  return vars;
}
